import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './contexts/LanguageContext';
import LanguageDropdown from './components/LanguageDropdown';
import './AccountCenter.css';

const AccountCenter = () => {
  const navigate = useNavigate();
  const { t, language, changeLanguage } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  
  // 添加编辑状态
  const [editUsername, setEditUsername] = useState(false);
  const [editIeltsDeadline, setEditIeltsDeadline] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newIeltsDeadline, setNewIeltsDeadline] = useState('');
  const [updateMessage, setUpdateMessage] = useState({ text: '', type: '' });
  
  // 添加兑换码状态
  const [showRedeemInput, setShowRedeemInput] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemError, setRedeemError] = useState('');
  
  // 导入统一的兑换码配置
  const validRedemptionCodes = [
    'TAL2025-N4ZQH-P8K7M',
    'TAL2025-R7LWF-B3X2D',
    'TAL2025-G6PMT-Y9H4S',
    'TAL2025-UX3AE-L5V9Q',
    'TAL2025-D2JKB-T8W6N',
    'TAL2025-Q8SCH-F1R7P',
    'TAL2025-H5VME-Z4T9K',
    'TAL2025-L1XRD-C6P8Y',
    'TAL2025-K3FZW-N7S2B',
    'TAL2025-P9YTE-G4M6U'
  ];
  
  // 验证兑换码
  const validateRedemptionCode = (code) => {
    // 检查localStorage中是否有已使用的兑换码记录
    const usedCodesJson = localStorage.getItem('talktastic_used_codes');
    let usedCodes = [];
    
    if (usedCodesJson) {
      try {
        usedCodes = JSON.parse(usedCodesJson);
      } catch (error) {
        console.error('读取已使用兑换码失败:', error);
        return { valid: false, message: language === 'en' ? 'System error, please try again' : '系统错误，请重试' };
      }
    }
    
    // 检查兑换码是否已被使用
    if (usedCodes.includes(code)) {
      return { valid: false, message: language === 'en' ? 'This code has already been used' : '此兑换码已被使用' };
    }
    
    // 检查兑换码是否有效
    if (!validRedemptionCodes.includes(code)) {
      return { valid: false, message: language === 'en' ? 'Invalid redemption code' : '无效的兑换码' };
    }
    
    // 兑换码有效，标记为已使用
    usedCodes.push(code);
    localStorage.setItem('talktastic_used_codes', JSON.stringify(usedCodes));
    
    return { valid: true };
  };
  
  // 检查用户登录状态
  useEffect(() => {
    console.log('AccountCenter: 检查用户登录状态');
    
    const storedUser = localStorage.getItem('talktastic_user');
    console.log('获取到的用户数据:', storedUser);
    
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        console.log('解析后的用户数据:', user);
        
        // 严格检查用户是否登录 - 必须同时满足isRegistered和isLoggedIn为true
        if (user && user.isRegistered && user.isLoggedIn) {
          console.log('用户已登录，设置登录状态');
          setIsLoggedIn(true);
          setUserData(user);
          setNewUsername(user.username || '');
          setNewIeltsDeadline(user.ieltsDeadline || '');
        } else {
          console.log('用户未登录，重定向到首页');
          // 重定向到首页而非登录页面
          navigate('/');
        }
      } catch (error) {
        console.error('读取用户信息失败:', error);
        navigate('/');
      }
    } else {
      console.log('未找到用户信息，重定向到首页');
      navigate('/');
    }
  }, [navigate]);

  // 导航回主页
  const handleBackToHome = () => {
    navigate('/');
  };

  // 导航到个人档案
  const handleGoToProfile = () => {
    navigate('/profile');
  };
  
  // 处理登出
  const handleLogout = () => {
    // 获取当前用户信息
    const userDataStr = localStorage.getItem('talktastic_user');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        
        // 更新用户登录状态为已登出
        userData.isLoggedIn = false;
        userData.isRegistered = false; // 确保不会被视为已注册状态
        
        // 更新localStorage中的用户数据
        localStorage.setItem('talktastic_user', JSON.stringify(userData));
        
        // 同时更新用户数据库的登录状态
        const usersJson = localStorage.getItem('talktastic_users');
        if (usersJson) {
          try {
            const users = JSON.parse(usersJson);
            if (users[userData.email]) {
              users[userData.email].isLoggedIn = false;
              localStorage.setItem('talktastic_users', JSON.stringify(users));
            }
          } catch (error) {
            console.error('更新用户数据库失败:', error);
          }
        }
        
        console.log('用户已登出，保留会员信息但标记为未登录状态');
      } catch (error) {
        console.error('处理用户登出失败:', error);
      }
    }
    
    // 直接跳转到首页，而不是登录页面
    navigate('/');
  };

  // 处理升级为付费会员
  const handleUpgrade = () => {
    if (userData) {
      // 设置固定会员截止日期为2024年5月15日
      const fixedExpiryDate = new Date(2024, 4, 15); // 注意：月份是从0开始的，所以4表示5月
      
      // 更新当前登录用户信息
      const updatedUser = {
        ...userData,
        isPaid: true,
        membershipExpiry: fixedExpiryDate.toISOString()
      };
      localStorage.setItem('talktastic_user', JSON.stringify(updatedUser));
      
      // 同时更新用户数据库
      const usersJson = localStorage.getItem('talktastic_users');
      if (usersJson) {
        try {
          const users = JSON.parse(usersJson);
          if (users[userData.email]) {
            users[userData.email].isPaid = true;
            users[userData.email].membershipExpiry = fixedExpiryDate.toISOString();
            localStorage.setItem('talktastic_users', JSON.stringify(users));
          }
        } catch (error) {
          console.error('更新用户数据失败:', error);
        }
      }
      
      setUserData(updatedUser);
      showUpdateMessage('success', language === 'en' 
        ? 'Upgrade successful! Your membership is valid until May 15, 2024. Score improvement reports and other core features are now available to you. Try them now!' 
        : '升级成功！您的会员有效期至2024年5月15日。现在提分报告以及其他新上线的核心功能将对您开放。快去试试吧！');
    }
  };

  // 显示更新消息
  const showUpdateMessage = (type, text) => {
    setUpdateMessage({ type, text });
    // 5秒后自动清除消息
    setTimeout(() => {
      setUpdateMessage({ text: '', type: '' });
    }, 5000);
  };

  // 打开用户名编辑
  const handleEditUsername = () => {
    setEditUsername(true);
  };

  // 打开雅思截止日期编辑
  const handleEditDeadline = () => {
    setEditIeltsDeadline(true);
  };

  // 保存用户名
  const handleSaveUsername = () => {
    if (!newUsername.trim()) {
      showUpdateMessage('error', language === 'en' ? 'Username cannot be empty' : '用户名不能为空');
      return;
    }

    if (userData) {
      // 更新当前登录用户信息
      const updatedUser = {
        ...userData,
        username: newUsername
      };
      localStorage.setItem('talktastic_user', JSON.stringify(updatedUser));
      
      // 同时更新用户数据库
      const usersJson = localStorage.getItem('talktastic_users');
      if (usersJson) {
        try {
          const users = JSON.parse(usersJson);
          if (users[userData.email]) {
            users[userData.email].username = newUsername;
            localStorage.setItem('talktastic_users', JSON.stringify(users));
          }
        } catch (error) {
          console.error('更新用户数据失败:', error);
        }
      }
      
      setUserData(updatedUser);
      setEditUsername(false);
      showUpdateMessage('success', language === 'en' ? 'Username updated successfully!' : '用户名更新成功！');
    }
  };

  // 保存雅思截止日期
  const handleSaveDeadline = () => {
    const deadline = parseInt(newIeltsDeadline);
    if (isNaN(deadline) || deadline < 0) {
      showUpdateMessage('error', language === 'en' ? 'Please enter a valid number of days' : '请输入有效的天数');
      return;
    }

    if (userData) {
      // 更新当前登录用户信息
      const updatedUser = {
        ...userData,
        ieltsDeadline: deadline
      };
      localStorage.setItem('talktastic_user', JSON.stringify(updatedUser));
      
      // 同时更新用户数据库
      const usersJson = localStorage.getItem('talktastic_users');
      if (usersJson) {
        try {
          const users = JSON.parse(usersJson);
          if (users[userData.email]) {
            users[userData.email].ieltsDeadline = deadline;
            localStorage.setItem('talktastic_users', JSON.stringify(users));
          }
        } catch (error) {
          console.error('更新用户数据失败:', error);
        }
      }
      
      setUserData(updatedUser);
      setEditIeltsDeadline(false);
      showUpdateMessage('success', language === 'en' ? 'IELTS deadline updated successfully!' : '雅思倒计时更新成功！');
    }
  };

  // 取消编辑
  const handleCancelEdit = (type) => {
    if (type === 'username') {
      setEditUsername(false);
      setNewUsername(userData?.username || '');
    } else if (type === 'deadline') {
      setEditIeltsDeadline(false);
      setNewIeltsDeadline(userData?.ieltsDeadline || '');
    }
  };

  // 处理还原为非付费会员（仅用于测试）
  const handleResetMemberStatus = () => {
    if (userData) {
      // 更新当前登录用户信息
      const updatedUser = {
        ...userData,
        isPaid: false,
        membershipExpiry: null
      };
      localStorage.setItem('talktastic_user', JSON.stringify(updatedUser));
      
      // 同时更新用户数据库
      const usersJson = localStorage.getItem('talktastic_users');
      if (usersJson) {
        try {
          const users = JSON.parse(usersJson);
          if (users[userData.email]) {
            users[userData.email].isPaid = false;
            users[userData.email].membershipExpiry = null;
            localStorage.setItem('talktastic_users', JSON.stringify(users));
          }
        } catch (error) {
          console.error('更新用户数据失败:', error);
        }
      }
      
      setUserData(updatedUser);
      showUpdateMessage('success', language === 'en' ? 'Member status reset successful!' : '会员状态重置成功！');
    }
  };

  // 处理兑换码提交
  const handleRedeemSubmit = async () => {
    if (!redeemCode.trim()) {
      setRedeemError(language === 'en' ? 'Please enter a redemption code' : '请输入兑换码');
      return;
    }
    
    // 检查用户是否已经是会员，如果是则不允许再次兑换
    if (userData?.isPaid) {
      setRedeemError(language === 'en' ? 'You are already a premium member' : '您已经是会员，无法重复兑换');
      return;
    }
    
    const validation = validateRedemptionCode(redeemCode.trim());
    
    if (!validation.valid) {
      setRedeemError(validation.message);
      return;
    }

    // 调用后端API处理兑换
    try {
      const response = await fetch('/api/users/membership', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          redeemCode: redeemCode.trim()
        })
      });

      const result = await response.json();

      if (result.success) {
        // 更新本地用户数据
        const updatedUser = {
          ...userData,
          isPaid: true,
          membershipExpiry: result.membershipExpiry
        };
        localStorage.setItem('talktastic_user', JSON.stringify(updatedUser));
        setUserData(updatedUser);

        // 同步更新用户数据库
        const usersJson = localStorage.getItem('talktastic_users');
        if (usersJson) {
          try {
            const users = JSON.parse(usersJson);
            if (users[userData.email]) {
              users[userData.email].isPaid = true;
              users[userData.email].membershipExpiry = result.membershipExpiry;
              localStorage.setItem('talktastic_users', JSON.stringify(users));
            }
          } catch (error) {
            console.error('更新本地用户数据失败:', error);
          }
        }

        showUpdateMessage('success', language === 'en' 
          ? `Redemption successful! Your membership is valid for 20 days.` 
          : `兑换成功！您的会员有效期为20天。`);
        
        setShowRedeemInput(false);
        setRedeemCode('');
        setRedeemError('');
      } else {
        setRedeemError(result.message || (language === 'en' ? 'Redemption failed' : '兑换失败'));
      }
    } catch (error) {
      console.error('兑换请求失败:', error);
      setRedeemError(language === 'en' ? 'Network error, please try again' : '网络错误，请重试');
    }
  };

  // 格式化日期显示
  const formatDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      
      // 为中文格式特别处理一下，使其更符合中文习惯
      if (language === 'zh-CN') {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${year}年${month}月${day}日`;
      } else {
        // 英文格式
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    } catch (error) {
      console.error('日期格式化错误:', error);
      return null;
    }
  };

  return (
    <div className="account-center-container">
      {/* 添加返回首页按钮 */}
      <button className="back-to-home-btn" onClick={handleBackToHome}>
        <i className="fas fa-arrow-left"></i> {language === 'en' ? 'Back to Home' : '返回首页'}
      </button>

      {/* 主要内容区域 */}
      <div className="account-content">
        <div className="account-header">
          <h1>{language === 'en' ? 'Account Center' : '账号中心'}</h1>
          <p>{language === 'en' ? 'Manage your account information and settings' : '管理您的账号信息和设置'}</p>
        </div>

        {/* 更新消息提示 */}
        {updateMessage.text && (
          <div className={`update-message ${updateMessage.type}`}>
            {updateMessage.text}
          </div>
        )}

        {/* 用户信息卡片 */}
        {isLoggedIn && (
          <div className="user-account-card">
            <div className="account-section-header">
              <h2>{language === 'en' ? 'Account Information' : '账号信息'}</h2>
            </div>
            <div className="account-details">
              <div className="account-item">
                <span className="account-label">{language === 'en' ? 'Email' : '邮箱'}:</span>
                <span className="account-value">{userData?.email}</span>
              </div>
              <div className="account-item">
                <span className="account-label">{language === 'en' ? 'Username' : '用户名'}:</span>
                {editUsername ? (
                  <div className="account-edit-container">
                    <input
                      type="text"
                      className="account-edit-input"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      placeholder={language === 'en' ? 'Enter username' : '输入用户名'}
                    />
                    <div className="account-edit-actions">
                      <button className="save-btn" onClick={handleSaveUsername}>
                        {language === 'en' ? 'Save' : '保存'}
                      </button>
                      <button className="cancel-btn" onClick={() => handleCancelEdit('username')}>
                        {language === 'en' ? 'Cancel' : '取消'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="account-value-with-edit">
                    <span className="account-value">{userData?.username || (language === 'en' ? 'Not set' : '未设置')}</span>
                    <button className="edit-btn" onClick={handleEditUsername}>
                      {language === 'en' ? 'Edit' : '编辑'}
                    </button>
                  </div>
                )}
              </div>
              <div className="account-item">
                <span className="account-label">{language === 'en' ? 'IELTS Deadline' : '雅思倒计时'}:</span>
                {editIeltsDeadline ? (
                  <div className="account-edit-container">
                    <input
                      type="number"
                      className="account-edit-input"
                      value={newIeltsDeadline}
                      onChange={(e) => setNewIeltsDeadline(e.target.value)}
                      placeholder={language === 'en' ? 'Enter days until exam' : '输入距离考试的天数'}
                      min="0"
                    />
                    <div className="account-edit-actions">
                      <button className="save-btn" onClick={handleSaveDeadline}>
                        {language === 'en' ? 'Save' : '保存'}
                      </button>
                      <button className="cancel-btn" onClick={() => handleCancelEdit('deadline')}>
                        {language === 'en' ? 'Cancel' : '取消'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="account-value-with-edit">
                    <span className="account-value">
                      {userData?.ieltsDeadline ? `${userData.ieltsDeadline} ${language === 'en' ? 'days' : '天'}` : (language === 'en' ? 'Not set' : '未设置')}
                    </span>
                    <button className="edit-btn" onClick={handleEditDeadline}>
                      {language === 'en' ? 'Edit' : '编辑'}
                    </button>
                  </div>
                )}
              </div>
              <div className="account-item">
                <span className="account-label">{language === 'en' ? 'Account Status' : '账号状态'}:</span>
                <span className={`account-value ${userData?.isPaid ? 'premium' : 'normal'}`}>
                  {userData?.isPaid 
                    ? (language === 'en' ? 'Premium' : '付费会员') 
                    : (language === 'en' ? 'Standard User' : '普通用户')}
                </span>
              </div>
              <div className="account-item">
                <span className="account-label">{language === 'en' ? 'Membership Expiry Date' : '会员有效期'}:</span>
                <span className={`account-value ${userData?.isPaid ? 'premium' : 'normal'}`}>
                  {userData?.membershipExpiry 
                    ? formatDate(userData.membershipExpiry)
                    : (language === 'en' ? 'Not a member' : '非会员')}
                </span>
              </div>
              <div className="account-item">
                <span className="account-label">{language === 'en' ? 'Login Time' : '登录时间'}:</span>
                <span className="account-value">
                  {userData?.loginTime 
                    ? new Date(userData.loginTime).toLocaleString(language === 'en' ? 'en-US' : 'zh-CN') 
                    : (language === 'en' ? 'Unknown' : '未知')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* 账号操作卡片 */}
        {isLoggedIn && (
          <div className="user-account-card">
            <div className="account-section-header">
              <h2>{language === 'en' ? 'Account Actions' : '账号操作'}</h2>
            </div>
            <div className="account-actions-container">
              <div className="account-action-description">
                {language === 'en' ? (
                  <p>
                    <strong>Dear English Learning Partner:</strong><br /><br />
                    
                    We uphold the principle of "User Experience First", and our product is currently in the development and optimization stage. To collect genuine feedback, <strong>we are not officially charging subscription fees yet</strong>, and we offer:
                    <br /><br />
                    
                    🎁 <strong>¥9.9 Internal Testing Super Value Plan</strong><br />
                    ✅ Current basic features<br />
                    ✅ Smart score improvement report<br />
                    ✅ Other exciting core features<br />
                    ✅ IELTS review framework articles<br />
                    <br />
                    
                    💡 <strong>Join the internal testing now, you can enjoy "Founding Member" status:</strong><br />
                    ✨ Priority experience of gamified oral training system<br />
                    ✨ Direct impact on the final form of the product<br />
                    ✨ Lock in future exclusive preferential qualifications<br />
                    <br />
                    
                    Chinese users can purchase membership redemption codes through the Little Red Book product link or the WeChat account below, or contact the WeChat below directly to purchase. Users from other countries please contact athenazhou71@gmail.com.<br />
                    After purchasing, click "Redeem Code" below to redeem. If you encounter any bugs or have any suggestions, please contact me promptly.
                    <br /><br />
                    
                    We promise: All features will be fully polished before launching a reasonable subscription model. Your every practice helps us build a better learning tool!
                  </p>
                ) : (
                  <p>
                    <strong>亲爱的英语学习伙伴:</strong><br /><br />
                    
                    我们坚持「用户体验至上」原则，目前产品仍处于开发优化阶段。为收集真实反馈，<strong>暂不开放正式订阅收费</strong>，特推出:
                    <br /><br />
                    
                    🎁 <strong>9.9元内测超值方案</strong><br />
                    ✅ 现有基础功能<br />
                    ✅ 智能提分报告<br />
                    ✅ 其他更惊喜的核心功能<br />
                    ✅ 雅思复习框架精华文章<br />
                    <br />
                    
                    💡 <strong>现在加入内测，您将以「创始会员」身份:</strong><br />
                    ✨ 优先体验游戏化口语训练系统<br />
                    ✨ 直接影响产品最终形态<br />
                    ✨ 锁定未来专属优惠资格<br />
                    <br />
                    
                    中国用户请通过小红书的商品链接或者以下的微信号购买会员兑换码，也可以直接联系下方微信购买。其他国家用户请联系athenazhou71@gmail.com。<br />
                    购买以后点击下方的"兑换会员"进行兑换。如果你遇到了任何bug或有任何建议，请及时与我联系。
                    <br /><br />
                    
                    我们承诺: 所有功能都会经过充分打磨后，再开启合理订阅模式。您的每一次练习，都在帮助我们打造更好的学习工具!
                  </p>
                )}
                <button 
                  className="language-toggle-btn" 
                  onClick={() => {
                    const newLang = language === 'en' ? 'zh' : 'en';
                    changeLanguage(newLang);
                  }}
                >
                  {language === 'en' ? '切换到中文' : 'Switch to English'}
                </button>
                
                {/* 添加微信和小红书图片 */}
                <div className="contact-images">
                  <div className="contact-image-container">
                    <img src="/images/微信.png" alt="WeChat QR Code" className="qr-code-image" />
                  </div>
                  <div className="contact-image-container">
                    <img src="/images/小红书.jpg" alt="Little Red Book QR Code" className="qr-code-image" />
                  </div>
                </div>
              </div>
              <div className="action-buttons">
                {/* 无论会员状态，都显示兑换会员按钮 */}
                <div className="redeem-container">
                  <button className="redeem-btn" onClick={() => setShowRedeemInput(true)}>
                    {language === 'en' ? 'Redeem Code' : '兑换会员'}
                  </button>
                  
                  {showRedeemInput && (
                    <div className="redeem-popup">
                      <div className="redeem-popup-content">
                        <button 
                          className="redeem-popup-close" 
                          onClick={() => {
                            setShowRedeemInput(false);
                            setRedeemCode('');
                            setRedeemError('');
                          }}
                        >
                          ×
                        </button>
                        <h3>{language === 'en' ? 'Redeem Code' : '兑换会员'}</h3>
                        <input
                          type="text"
                          value={redeemCode}
                          onChange={(e) => {
                            setRedeemCode(e.target.value);
                            setRedeemError(''); // 清除之前的错误信息
                          }}
                          placeholder={language === 'en' ? 'Please enter your authorization code' : '请输入授权码'}
                          className="redeem-popup-input"
                          autoFocus
                        />
                        {redeemError && (
                          <p className="redeem-error">
                            {redeemError}
                          </p>
                        )}
                        <p className="redeem-note">
                          {language === 'en' ? 'Note: Authorization code is invalidated once redeemed, and cannot be used again!' : '注: 授权码一旦兑换成功即失效，无法再次使用!'}
                        </p>
                        <div className="redeem-popup-buttons">
                          <button 
                            className="redeem-cancel-btn"
                            onClick={() => {
                              setShowRedeemInput(false);
                              setRedeemCode('');
                              setRedeemError('');
                            }}
                          >
                            {language === 'en' ? 'Cancel' : '取消'}
                          </button>
                          <button 
                            className="redeem-confirm-btn"
                            onClick={handleRedeemSubmit}
                          >
                            {language === 'en' ? 'Redeem' : '兑换'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <button className="profile-btn" onClick={handleGoToProfile}>
                  {language === 'en' ? 'Go to Profile' : '前往个人档案'}
                </button>
                <button className="logout-btn" onClick={handleLogout}>
                  {language === 'en' ? 'Logout' : '退出登录'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountCenter; 
